import React, { useState, useEffect, useCallback, useMemo } from "react";
import { withRouter } from "react-router-dom";
import axios from "axios";
import {
  FileText, Briefcase, UsersRound, Mail, MessageSquare, User, FileDown,
  Settings, LayoutDashboard, Plus, Search, ArrowUpRight, FolderKanban,
} from "lucide-react";

import setAuthToken from "../../utils/setAuthToken";
import { CommandPalette, SearchInput } from "../../design-system";

const NAV_ITEMS = [
  { id: "nav-dashboard",  label: "Tableau de bord",  path: "/dashboard",                Icon: LayoutDashboard },
  { id: "nav-articles",   label: "Articles",         path: "/dashboard/articles",       Icon: FileText },
  { id: "nav-projects",   label: "Projets",          path: "/dashboard/projects",       Icon: FolderKanban },
  { id: "nav-stages",     label: "Stages",           path: "/dashboard/stages",         Icon: Briefcase },
  { id: "nav-pros",       label: "Professionnels",   path: "/dashboard/professionals",  Icon: UsersRound },
  { id: "nav-newsletter", label: "Newsletter",       path: "/dashboard/newsletter",     Icon: Mail },
  { id: "nav-contacts",   label: "Contacts",         path: "/dashboard/contacts",       Icon: MessageSquare },
  { id: "nav-users",      label: "Utilisateurs",     path: "/dashboard/users",          Icon: User },
  { id: "nav-cvs",        label: "CV",               path: "/dashboard/cvs",            Icon: FileDown },
  { id: "nav-profile",    label: "Mon profil",       path: "/dashboard/profile",        Icon: Settings },
];

const QUICK_ACTIONS = [
  { id: "act-new-article",  label: "Nouvel article",      path: "/dashboard/articles/new",       Icon: Plus, hint: "Créer un brouillon" },
  { id: "act-new-project",  label: "Nouveau projet",      path: "/dashboard/projects/new",       Icon: Plus, hint: "Créer une page projet" },
  { id: "act-new-stage",    label: "Nouveau stage",       path: "/dashboard/stages/new",         Icon: Plus, hint: "Ajouter une expérience" },
  { id: "act-new-pro",      label: "Nouveau professionnel", path: "/dashboard/professionals/new", Icon: Plus, hint: "Emploi / projet / certif" },
  { id: "act-new-user",     label: "Nouvel utilisateur",  path: "/dashboard/users/new",          Icon: Plus, hint: "Compte admin" },
];

function useDebounced(value, delay = 200) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

/**
 * AdminCommandPalette — Recherche universelle pour l'admin.
 *
 * Deux modes :
 * 1. Trigger : rend un <SearchInput readOnly> qui ouvre la palette au clic
 * 2. Auto : écoute ⌘K / Ctrl+K globalement pour ouvrir
 */
function AdminCommandPaletteRaw({ history, asTrigger = true }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounced(query, 250);
  const [results, setResults] = useState({
    articles: [], contacts: [], users: [], stages: [], professionals: [], projects: [],
  });
  const [searching, setSearching] = useState(false);

  const close = useCallback(() => {
    setOpen(false);
    setQuery("");
  }, []);

  // Shortcut clavier ⌘K / Ctrl+K
  useEffect(() => {
    const onKey = (e) => {
      const isShortcut = (e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K");
      if (isShortcut) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Recherche réelle sur APIs
  useEffect(() => {
    let cancelled = false;
    if (!open || !debouncedQuery || debouncedQuery.length < 2) {
      setResults({ articles: [], contacts: [], users: [], stages: [], professionals: [], projects: [] });
      setSearching(false);
      return;
    }
    setSearching(true);
    const token = localStorage.getItem("jwtToken");
    if (token) setAuthToken(token);
    const q = encodeURIComponent(debouncedQuery);

    Promise.allSettled([
      axios.get(`/api/articles?limit=6`),
      axios.get(`/api/contacts?limit=6`),
      axios.get(`/api/users`),
      axios.get(`/api/stages`),
      axios.get(`/api/professionals?limit=6`),
      axios.get(`/api/projects?limit=20`),
    ]).then((res) => {
      if (cancelled) return;
      const lower = debouncedQuery.toLowerCase();
      const matchString = (s) => (s || "").toLowerCase().includes(lower);

      const articles = (res[0].value?.data?.articles || []).filter(
        (a) => matchString(a.title) || matchString(a.slug) || matchString(a.excerpt),
      ).slice(0, 5);
      const contacts = (res[1].value?.data?.contacts || []).filter(
        (c) => matchString(c.name) || matchString(c.email) || matchString(c.subject),
      ).slice(0, 5);
      const users = (res[2].value?.data?.users || []).filter(
        (u) => matchString(u.name) || matchString(u.email),
      ).slice(0, 5);
      const stages = (res[3].value?.data?.stages || []).filter(
        (s) => matchString(s.title) || matchString(s.company),
      ).slice(0, 5);
      const professionals = (res[4].value?.data?.professionals || []).filter(
        (p) => matchString(p.title) || matchString(p.company),
      ).slice(0, 5);

      const projects = (res[5].value?.data?.projects || []).filter(
        (p) => matchString(p.title) || matchString(p.slug) || matchString(p.tagline),
      ).slice(0, 5);

      setResults({ articles, contacts, users, stages, professionals, projects });
      setSearching(false);
    });
    return () => { cancelled = true; };
  }, [debouncedQuery, open]);

  const groups = useMemo(() => {
    const q = query.toLowerCase();
    const matchNav = (label) => !q || label.toLowerCase().includes(q);

    const filteredNav = NAV_ITEMS.filter((n) => matchNav(n.label));
    const filteredActions = QUICK_ACTIONS.filter((a) => matchNav(a.label));

    const out = [];

    if (filteredNav.length > 0) {
      out.push({
        label: "Aller à",
        items: filteredNav.map((n) => ({
          id: n.id,
          label: n.label,
          icon: <n.Icon className="w-3.5 h-3.5" strokeWidth={1.8} />,
          onSelect: () => history.push(n.path),
        })),
      });
    }

    if (filteredActions.length > 0) {
      out.push({
        label: "Créer",
        items: filteredActions.map((a) => ({
          id: a.id,
          label: a.label,
          hint: a.hint,
          icon: <a.Icon className="w-3.5 h-3.5" strokeWidth={1.8} />,
          onSelect: () => history.push(a.path),
        })),
      });
    }

    if (results.articles.length > 0) {
      out.push({
        label: "Articles",
        items: results.articles.map((a) => ({
          id: `art-${a.id}`,
          label: a.title,
          hint: a.excerpt?.slice(0, 60) || a.slug,
          icon: <FileText className="w-3.5 h-3.5" strokeWidth={1.8} />,
          onSelect: () => history.push(`/dashboard/articles/${a.id}/view`),
        })),
      });
    }
    if (results.projects.length > 0) {
      out.push({
        label: "Projets",
        items: results.projects.map((p) => ({
          id: `project-${p.id}`,
          label: p.title,
          hint: p.tagline || p.slug,
          icon: <FolderKanban className="w-3.5 h-3.5" strokeWidth={1.8} />,
          onSelect: () => history.push(`/dashboard/projects/${p.id}`),
        })),
      });
    }
    if (results.stages.length > 0) {
      out.push({
        label: "Stages",
        items: results.stages.map((s) => ({
          id: `stage-${s.id}`,
          label: s.title,
          hint: `${s.company || ""} · ${s.period || ""}`,
          icon: <Briefcase className="w-3.5 h-3.5" strokeWidth={1.8} />,
          onSelect: () => history.push(`/dashboard/stages/${s.id}`),
        })),
      });
    }
    if (results.professionals.length > 0) {
      out.push({
        label: "Professionnels",
        items: results.professionals.map((p) => ({
          id: `pro-${p.id}`,
          label: p.title,
          hint: `${p.company || ""} · ${p.period || ""}`,
          icon: <UsersRound className="w-3.5 h-3.5" strokeWidth={1.8} />,
          onSelect: () => history.push(`/dashboard/professionals/${p.id}`),
        })),
      });
    }
    if (results.contacts.length > 0) {
      out.push({
        label: "Contacts",
        items: results.contacts.map((c) => ({
          id: `contact-${c.id}`,
          label: c.subject || c.name,
          hint: `${c.name} · ${c.email}`,
          icon: <MessageSquare className="w-3.5 h-3.5" strokeWidth={1.8} />,
          onSelect: () => history.push(`/dashboard/contacts/${c.id}`),
        })),
      });
    }
    if (results.users.length > 0) {
      out.push({
        label: "Utilisateurs",
        items: results.users.map((u) => ({
          id: `user-${u.id}`,
          label: u.name || u.email,
          hint: u.email,
          icon: <User className="w-3.5 h-3.5" strokeWidth={1.8} />,
          onSelect: () => history.push(`/dashboard/users/${u.id}`),
        })),
      });
    }

    return out;
  }, [query, results, history]);

  return (
    <>
      {asTrigger && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="w-full inline-flex items-center gap-2.5 h-9 px-3 rounded-md bg-bg-2 border border-border text-sm text-mute hover:bg-surface hover:border-border-strong transition-colors"
          aria-label="Ouvrir la recherche universelle"
        >
          <Search className="w-4 h-4 text-mute" strokeWidth={1.8} />
          <span className="flex-1 text-left">Recherche universelle…</span>
          <kbd className="font-mono text-[10.5px] text-mute border border-border rounded px-1.5 py-0.5 bg-surface">⌘ K</kbd>
        </button>
      )}
      <CommandPalette
        isOpen={open}
        onClose={close}
        query={query}
        onQueryChange={setQuery}
        groups={groups}
        isLoading={searching}
        placeholder="Tape pour chercher articles, contacts, utilisateurs…"
        emptyMessage={
          query.length < 2
            ? "Tape au moins 2 caractères pour chercher dans tout l'admin."
            : "Aucun résultat pour cette recherche."
        }
      />
    </>
  );
}

export default withRouter(AdminCommandPaletteRaw);
